import { collection, doc, deleteDoc, getDoc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { STOCK_METER_PRODUCT_TYPES } from '../constants';

export const isTulKanatProductType = (productType) => STOCK_METER_PRODUCT_TYPES.includes(productType);

// Adet bazlı stok takibi: Rustik ve Aksesuar
const QUANTITY_STOCK_PRODUCT_TYPES = ['rustik', 'aksesuar'];
export const isQuantityStockProductType = (productType) => QUANTITY_STOCK_PRODUCT_TYPES.includes(productType);

export const applyQuantityStockDelta = async (db, order, deltaQty) => {
  if (!deltaQty || Math.abs(deltaQty) < 0.001) return { success: true, skipped: true };

  const product = await findProductItemForOrder(db, order);
  if (!product) {
    return { success: false, reason: 'product_not_found', productName: order?.productName };
  }

  const currentStock = parseFloat(product.stock) || 0;
  const newStock = Math.max(0, Number((currentStock - deltaQty).toFixed(2)));

  await updateDoc(doc(db, 'productItems', product.id), {
    stock: newStock,
    updatedAt: new Date()
  });

  return { success: true, productId: product.id, productName: product.name, previousStock: currentStock, newStock, deltaQty };
};

export const deductQuantityStockForOrder = async (db, order) => {
  if (!isQuantityStockProductType(order.productType)) return { success: true, skipped: true };
  const qty = parseFloat(order.quantity) || 0;
  if (qty <= 0) return { success: false, reason: 'zero_amount' };
  return applyQuantityStockDelta(db, order, qty);
};

export const restoreQuantityStockForOrder = async (db, order) => {
  if (!isQuantityStockProductType(order.productType)) return { success: true, skipped: true };
  const qty = parseFloat(order.quantity) || 0;
  if (qty <= 0) return { success: false, reason: 'zero_amount' };
  return applyQuantityStockDelta(db, order, -qty); // negatif = stoğa ekle
};

export const adjustQuantityStockOnOrderEdit = async (db, oldOrder, newOrder) => {
  const oldIsQty = isQuantityStockProductType(oldOrder.productType);
  const newIsQty = isQuantityStockProductType(newOrder.productType);

  if (!oldIsQty && !newIsQty) return { success: true, skipped: true };

  // Eski stoku geri yükle
  if (oldIsQty) {
    const oldQty = parseFloat(oldOrder.quantity) || 0;
    if (oldQty > 0) await applyQuantityStockDelta(db, oldOrder, -oldQty);
  }
  // Yeni stoku düş
  if (newIsQty) {
    const newQty = parseFloat(newOrder.quantity) || 0;
    if (newQty > 0) await applyQuantityStockDelta(db, newOrder, newQty);
  }

  return { success: true };
};

export const resolveOrderIsWholesale = (isWholesale, customerInfo) =>
  isWholesale || customerInfo?.type === 'wholesale' || customerInfo?.type === 'bayi';

const normalizeProductName = (name) => (name || '').trim().toLowerCase().replace(/\s+/g, ' ');

export const calculateTulKanatStockUsageMeters = (order, isWholesale) => {
  const qty = parseFloat(order.quantity) || 1;

  if (isWholesale) {
    const widthM = parseFloat(order.width) || 0;
    return Number((widthM * qty).toFixed(2));
  }

  const usedMeters = parseFloat(order.usedM2) || 0;
  return Number((usedMeters * qty).toFixed(2));
};

export const findProductItemForOrder = async (db, order) => {
  if (order?.productItemId) {
    const productSnap = await getDoc(doc(db, 'productItems', order.productItemId));
    if (productSnap.exists()) {
      return { id: productSnap.id, ...productSnap.data() };
    }
  }

  const normalizedName = normalizeProductName(order?.productName);
  const productType = order?.productType;
  if (!normalizedName || !productType) return null;

  const snapshot = await getDocs(
    query(collection(db, 'productItems'), where('productType', '==', productType))
  );

  return (
    snapshot.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .find((p) => normalizeProductName(p.name) === normalizedName) || null
  );
};

export const applyTulKanatStockDelta = async (db, order, deltaMeters) => {
  if (!deltaMeters || Math.abs(deltaMeters) < 0.0001) {
    return { success: true, skipped: true };
  }

  const product = await findProductItemForOrder(db, order);
  if (!product) {
    return { success: false, reason: 'product_not_found', productName: order?.productName, deltaMeters };
  }

  const currentStock = parseFloat(product.stock) || 0;
  const newStock = Number((currentStock - deltaMeters).toFixed(2));

  await updateDoc(doc(db, 'productItems', product.id), {
    stock: newStock,
    updatedAt: new Date()
  });

  return {
    success: true,
    productId: product.id,
    productName: product.name,
    previousStock: currentStock,
    newStock,
    deltaMeters
  };
};

export const restoreTulKanatStockForOrder = async (db, order, isWholesale) => {
  if (!isTulKanatProductType(order.productType)) {
    return { success: true, skipped: true };
  }

  const usage = calculateTulKanatStockUsageMeters(order, isWholesale);
  if (!usage || usage <= 0) {
    return { success: false, reason: 'zero_amount' };
  }

  return applyTulKanatStockDelta(db, order, -usage);
};

export const adjustTulKanatStockOnOrderEdit = async (db, oldOrder, newOrder, isWholesale) => {
  const oldIsTulKanat = isTulKanatProductType(oldOrder.productType);
  const newIsTulKanat = isTulKanatProductType(newOrder.productType);
  const results = [];

  if (oldIsTulKanat) {
    const oldUsage = calculateTulKanatStockUsageMeters(oldOrder, isWholesale);
    if (oldUsage > 0) {
      const oldName = normalizeProductName(oldOrder.productName);
      const newName = normalizeProductName(newOrder.productName);
      const sameProduct =
        newIsTulKanat &&
        oldOrder.productType === newOrder.productType &&
        (oldOrder.productItemId && newOrder.productItemId
          ? oldOrder.productItemId === newOrder.productItemId
          : oldName === newName);

      if (sameProduct) {
        const newUsage = calculateTulKanatStockUsageMeters(newOrder, isWholesale);
        const delta = Number((newUsage - oldUsage).toFixed(2));
        if (delta !== 0) {
          results.push(await applyTulKanatStockDelta(db, newOrder, delta));
        }
      } else {
        results.push(await applyTulKanatStockDelta(db, oldOrder, -oldUsage));
      }
    }
  }

  if (newIsTulKanat) {
    const oldName = normalizeProductName(oldOrder.productName);
    const newName = normalizeProductName(newOrder.productName);
    const sameProduct =
      oldIsTulKanat &&
      oldOrder.productType === newOrder.productType &&
      (oldOrder.productItemId && newOrder.productItemId
        ? oldOrder.productItemId === newOrder.productItemId
        : oldName === newName);

    if (!sameProduct) {
      const newUsage = calculateTulKanatStockUsageMeters(newOrder, isWholesale);
      if (newUsage > 0) {
        results.push(await applyTulKanatStockDelta(db, newOrder, newUsage));
      }
    }
  }

  const failed = results.find((r) => r.success === false);
  if (failed) {
    return { success: false, reason: failed.reason, results };
  }

  return { success: true, results };
};

export const updateOrderWithTulKanatStockAdjustment = async (db, orderId, oldOrder, newOrder, isWholesale) => {
  const stockResult = await adjustTulKanatStockOnOrderEdit(db, oldOrder, newOrder, isWholesale);
  await updateDoc(doc(db, 'orders', orderId), newOrder);
  return stockResult;
};

export const createOrderWithTulKanatStockDeduction = async (db, order, isWholesale) => {
  const deductedMeters = calculateTulKanatStockUsageMeters(order, isWholesale);
  const orderRef = doc(collection(db, 'orders'));
  await setDoc(orderRef, order);

  if (!deductedMeters || deductedMeters <= 0) {
    return { orderId: orderRef.id, stock: { success: false, reason: 'zero_amount' } };
  }

  try {
    const stockResult = await applyTulKanatStockDelta(db, order, deductedMeters);
    return { orderId: orderRef.id, stock: stockResult };
  } catch (error) {
    console.error('Stok düşürme hatası:', error);
    return {
      orderId: orderRef.id,
      stock: { success: false, reason: 'update_failed', message: error.message }
    };
  }
};

export const deleteOrderWithTulKanatStockRestore = async (db, orderId, order, isWholesale) => {
  const stockResult = await restoreTulKanatStockForOrder(db, order, isWholesale);
  await deleteDoc(doc(db, 'orders', orderId));
  return stockResult;
};

export const notifyStockOperationResult = (stockResult, order, t, showToast) => {
  if (!stockResult || stockResult.success !== false) return;

  if (stockResult.reason === 'product_not_found') {
    showToast(t('orders.details.stock.productNotFound', { product: order.productName }));
    return;
  }

  if (stockResult.reason === 'zero_amount') {
    showToast(t('orders.details.stock.zeroAmount'));
    return;
  }

  if (stockResult.reason === 'update_failed') {
    showToast(t('orders.details.stock.deductError', { message: stockResult.message || '' }));
  }
};
